import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { colors } from "../../theme";
import ValueText from "./ValueText";
import { DuplicateAuditListItem } from "../../types/duplicateAudit";
import {
  AssessmentKind,
  DiffSegment,
  diffValues,
  displayValue,
  formatDob,
  formatSimilarityPercent,
  getSimilarityAssessment,
} from "../../utils/duplicateAudit";

const KIND_COLORS: Record<AssessmentKind, string> = {
  exact: colors.mui.success,
  close: colors.mui.warning,
  different: colors.mui.error,
  none: "text.disabled",
};

interface Row {
  field: string;
  newValue: string;
  matchValue: string;
  similarity: number | null;
  supplied: boolean; // both sides have a value, so a difference can be highlighted
}

const supplied = (a: unknown, b: unknown) => !!a && !!b;

export const buildComparisonRows = (a: DuplicateAuditListItem): Row[] => [
  {
    field: "First name",
    newValue: displayValue(a.customerFirstName),
    matchValue: displayValue(a.matchingCustomerFirstName),
    similarity: a.firstNameSimilarity,
    supplied: supplied(a.customerFirstName, a.matchingCustomerFirstName),
  },
  {
    field: "Last name",
    newValue: displayValue(a.customerLastName),
    matchValue: displayValue(a.matchingCustomerLastName),
    similarity: a.lastNameSimilarity,
    supplied: supplied(a.customerLastName, a.matchingCustomerLastName),
  },
  {
    field: "Date of birth",
    newValue: formatDob(a.customerDateOfBirth),
    matchValue: formatDob(a.matchingCustomerDateOfBirth),
    similarity: a.dateOfBirthSimilarity,
    supplied: supplied(a.customerDateOfBirth, a.matchingCustomerDateOfBirth),
  },
  {
    field: "Postcode",
    newValue: displayValue(a.customerPrimaryPostcode),
    matchValue: displayValue(a.matchingCustomerPrimaryPostcode),
    similarity: a.postcodeSimilarity,
    supplied: supplied(a.customerPrimaryPostcode, a.matchingCustomerPrimaryPostcode),
  },
  {
    field: "Email",
    newValue: displayValue(a.customerEmail),
    matchValue: displayValue(a.matchingCustomerEmail),
    similarity: a.emailSimilarity,
    supplied: supplied(a.customerEmail, a.matchingCustomerEmail),
  },
  {
    field: "Primary phone",
    newValue: displayValue(a.customerPrimaryPhone),
    matchValue: displayValue(a.matchingCustomerPrimaryPhone),
    similarity: a.phoneSimilarity,
    supplied: supplied(a.customerPrimaryPhone, a.matchingCustomerPrimaryPhone),
  },
];

function Highlighted({ segments }: { segments: DiffSegment[] }) {
  return (
    <>
      {segments.map((seg, i) =>
        seg.changed ? (
          <Box
            key={i}
            component="mark"
            sx={{
              bgcolor: "#FFF176", // highlighter-pen yellow
              color: "inherit",
              fontWeight: 600,
              borderRadius: "2px",
              px: "1px",
            }}
          >
            {seg.text}
          </Box>
        ) : (
          <span key={i}>{seg.text}</span>
        )
      )}
    </>
  );
}

export default function FieldComparisonTable({ audit }: { audit: DuplicateAuditListItem }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{ "& th": { bgcolor: colors.primary.dark, color: colors.text.white, fontWeight: 700 } }}
          >
            <TableCell>Field</TableCell>
            <TableCell>New customer</TableCell>
            <TableCell>Potential match</TableCell>
            <TableCell>Score</TableCell>
            <TableCell>Assessment</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {buildComparisonRows(audit).map((row) => {
            const assessment = getSimilarityAssessment(row.similarity);
            const diff = row.supplied ? diffValues(row.newValue, row.matchValue) : null;
            return (
              <TableRow key={row.field}>
                <TableCell>{row.field}</TableCell>
                <TableCell>{diff ? <Highlighted segments={diff.a} /> : <ValueText value={row.newValue} />}</TableCell>
                <TableCell>{diff ? <Highlighted segments={diff.b} /> : <ValueText value={row.matchValue} />}</TableCell>
                <TableCell>{formatSimilarityPercent(row.similarity)}</TableCell>
                <TableCell
                  sx={{
                    color: KIND_COLORS[assessment.kind],
                    fontWeight: 700,
                    fontStyle: assessment.kind === "none" ? "italic" : "normal",
                  }}
                >
                  {assessment.label}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
